import { useState, useEffect, useRef, useCallback } from 'react'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import {
  getDebugEnabled,
  getComponentRegistry,
  getHighlightedComponents,
  subscribeToDebug,
  subscribeToHighlights,
  toggleDebugMode,
  toggleComponentHighlight
} from './debugCore'

/**
 * Debug UI Components
 * 
 * React components for debug mode controls and component selection.
 * Separated from core state management for better modularity.
 */

/**
 * Debug Toggle Button
 * Button to toggle debug mode on/off
 * Can be inline (default) or floating (when floating prop is true)
 */
export function DebugToggle({ floating = false, showSelector = false }) {
  const [debugEnabled, setDebugEnabled] = useState(getDebugEnabled())
  const [showPanel, setShowPanel] = useState(false)

  useEffect(() => {
    return subscribeToDebug(setDebugEnabled)
  }, [])

  // Only show if debug mode available
  if (import.meta.env.VITE_DEBUG_MODE !== 'true') {
    return null
  }

  const baseStyle = {
    backgroundColor: debugEnabled ? '#10b981' : '#ef4444',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontFamily: 'monospace',
    transition: 'all 0.2s ease'
  }

  const floatingStyle = floating ? {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    zIndex: 999999,
    borderRadius: '50%',
    width: '60px',
    height: '60px',
    fontSize: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  } : {
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
  }

  return (
    <>
      <button
        onClick={toggleDebugMode}
        style={{ ...baseStyle, ...floatingStyle }}
        title="Toggle Debug Mode (Admin Only)"
      >
        {floating ? (
          <>
            <div style={{ width: '24px', height: '24px', marginBottom: '2px' }}>
              {debugEnabled ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 01-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44.125 2.104.52 4.136 1.153 6.06M12 12.75a2.25 2.25 0 002.248-2.354M12 12.75a2.25 2.25 0 01-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 00-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.734 3.734 0 01.4-2.253M12 8.25a2.25 2.25 0 00-2.248 2.146M12 8.25a2.25 2.25 0 012.248 2.146M8.683 5a6.032 6.032 0 01-1.155-1.002c.07-.63.27-1.222.574-1.747m.581 2.749A3.75 3.75 0 0115.318 5m0 0c.427-.283.815-.62 1.155-.999a4.471 4.471 0 00-.575-1.752M4.921 6a24.048 24.048 0 00-.392 3.314c1.668.546 3.416.914 5.223 1.082M19.08 6c.205 1.08.337 2.187.392 3.314a23.882 23.882 0 01-5.223 1.082" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
              )}
            </div>
            <div style={{ fontSize: '9px' }}>
              {debugEnabled ? 'ON' : 'OFF'}
            </div>
          </>
        ) : (
          <>
            <span style={{ width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center' }}>
              {debugEnabled ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 01-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44.125 2.104.52 4.136 1.153 6.06M12 12.75a2.25 2.25 0 002.248-2.354M12 12.75a2.25 2.25 0 01-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 00-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.734 3.734 0 01.4-2.253M12 8.25a2.25 2.25 0 00-2.248 2.146M12 8.25a2.25 2.25 0 012.248 2.146M8.683 5a6.032 6.032 0 01-1.155-1.002c.07-.63.27-1.222.574-1.747m.581 2.749A3.75 3.75 0 0115.318 5m0 0c.427-.283.815-.62 1.155-.999a4.471 4.471 0 00-.575-1.752M4.921 6a24.048 24.048 0 00-.392 3.314c1.668.546 3.416.914 5.223 1.082M19.08 6c.205 1.08.337 2.187.392 3.314a23.882 23.882 0 01-5.223 1.082" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
              )}
            </span>
            <span>
              Debug: {debugEnabled ? 'ON' : 'OFF'}
            </span>
          </>
        )}
      </button>
      
      {showSelector && debugEnabled && (
        <button
          onClick={() => setShowPanel(!showPanel)}
          style={{
            ...baseStyle,
            backgroundColor: '#3B82F6',
            marginLeft: floating ? '0' : '8px',
            marginTop: floating ? '8px' : '0',
            borderRadius: floating ? '50%' : '8px',
            width: floating ? '60px' : 'auto',
            height: floating ? '60px' : 'auto',
            padding: floating ? '0' : '8px 16px'
          }}
          title="Select components to highlight"
        >
          {floating ? (
            <div style={{ width: '24px', height: '24px' }}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
              </svg>
            </div>
          ) : (
            <>
              <span style={{ width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center' }}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                </svg>
              </span>
              <span>Components</span>
            </>
          )}
        </button>
      )}
      
      {showPanel && debugEnabled && <ComponentSelector onClose={() => setShowPanel(false)} />}
    </>
  )
}

/**
 * Component Selector Button (without debug toggle)
 * Only shows the selector button when debug mode is enabled
 * Use this on frontend pages where you don't want the debug toggle visible
 */
export function ComponentSelectorButton({ floating = false }) {
  const [debugEnabled, setDebugEnabled] = useState(getDebugEnabled())
  const [showPanel, setShowPanel] = useState(false)
  const [pos, setPos] = useState(null)
  const buttonRef = useRef(null)
  const dragState = useRef({ dragging: false, moved: false })

  useEffect(() => {
    return subscribeToDebug(setDebugEnabled)
  }, [])

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0 || !floating) return
    e.preventDefault()
    const rect = buttonRef.current.getBoundingClientRect()
    const startX = e.clientX
    const startY = e.clientY
    const elX = rect.left
    const elY = rect.top
    dragState.current = { dragging: true, moved: false }

    const onMouseMove = (e) => {
      const dx = e.clientX - startX
      const dy = e.clientY - startY
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragState.current.moved = true
      setPos({ x: elX + dx, y: elY + dy })
    }
    const onMouseUp = () => {
      dragState.current.dragging = false
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [floating])

  const handleClick = useCallback(() => {
    if (dragState.current.moved) { dragState.current.moved = false; return }
    setShowPanel(p => !p)
  }, [])

  // Only show if debug mode is enabled at build time AND runtime
  if (import.meta.env.VITE_DEBUG_MODE !== 'true' || !debugEnabled) {
    return null
  }

  const baseStyle = {
    backgroundColor: '#3B82F6',
    color: 'white',
    border: 'none',
    cursor: floating ? 'grab' : 'pointer',
    fontWeight: 'bold',
    fontFamily: 'monospace',
    transition: pos ? 'none' : 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  }

  const floatingStyle = floating ? {
    position: 'fixed',
    ...(pos
      ? { left: `${pos.x}px`, top: `${pos.y}px` }
      : { bottom: '20px', left: '50%', transform: 'translateX(-50%)' }
    ),
    zIndex: 999999,
    borderRadius: '50%',
    width: '60px',
    height: '60px',
    fontSize: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  } : {
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  }

  return (
    <>
      <button
        ref={floating ? buttonRef : undefined}
        onMouseDown={floating ? onMouseDown : undefined}
        onClick={handleClick}
        style={{ ...baseStyle, ...floatingStyle }}
        title="Select components to highlight"
      >
        {floating ? (
          <div style={{ width: '24px', height: '24px' }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
            </svg>
          </div>
        ) : (
          <>
            <span style={{ width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center' }}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
              </svg>
            </span>
            <span>Components</span>
          </>
        )}
      </button>
      
      {showPanel && <ComponentSelector onClose={() => setShowPanel(false)} />}
    </>
  )
}

/**
 * Component Selector Panel
 * Shows list of all registered components and allows selective highlighting
 */
export function ComponentSelector({ onClose }) {
  const componentRegistry = getComponentRegistry()
  const highlightedComponents = getHighlightedComponents()
  const [components, setComponents] = useState([...componentRegistry.values()])
  const [filter, setFilter] = useState('')

  useEffect(() => {
    return subscribeToHighlights(() => {
      setComponents([...componentRegistry.values()])
    })
  }, [])

  const filteredComponents = components.filter(comp => 
    comp.displayName.toLowerCase().includes(filter.toLowerCase()) ||
    comp.filePath.toLowerCase().includes(filter.toLowerCase())
  )

  const handleToggleAll = () => {
    if (highlightedComponents.size === componentRegistry.size) {
      // Clear all
      highlightedComponents.clear()
    } else {
      // Highlight all
      componentRegistry.forEach((_, id) => highlightedComponents.add(id))
    }
    localStorage.setItem('highlighted_components', JSON.stringify([...highlightedComponents]))
    // Manually notify listeners
    const listeners = []
    subscribeToHighlights(cb => listeners.push(cb))
    listeners.forEach(cb => cb([...componentRegistry.values()]))
  }

  return (
    <div style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'white',
      border: '2px solid #3B82F6',
      borderRadius: '12px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
      zIndex: 1000000,
      width: '500px',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#3B82F6',
        color: 'white',
        borderRadius: '10px 10px 0 0'
      }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
          Component Selector ({components.length})
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '0',
            lineHeight: '1'
          }}
          title="Close"
        >
          ×
        </button>
      </div>

      {/* Search and Controls */}
      <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
        <input
          type="text"
          placeholder="Filter components..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            marginBottom: '8px'
          }}
        />
        <button
          onClick={handleToggleAll}
          style={{
            width: '100%',
            padding: '8px 12px',
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          {highlightedComponents.size === componentRegistry.size ? 'Clear All' : 'Highlight All'}
        </button>
      </div>

      {/* Component List */}
      <div style={{
        overflowY: 'auto',
        flex: 1,
        padding: '8px'
      }}>
        {filteredComponents.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
            No components found
          </div>
        ) : (
          filteredComponents.map((comp) => {
            const componentId = `${comp.displayName}-${comp.filePath}`
            const isHighlighted = highlightedComponents.has(componentId)
            
            return (
              <div
                key={componentId}
                onClick={() => toggleComponentHighlight(componentId)}
                style={{
                  padding: '12px',
                  margin: '4px 0',
                  backgroundColor: isHighlighted ? '#dbeafe' : 'white',
                  border: `2px solid ${isHighlighted ? '#3B82F6' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isHighlighted ? '#bfdbfe' : '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isHighlighted ? '#dbeafe' : 'white'}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#111827',
                      marginBottom: '4px'
                    }}>
                      {comp.displayName}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      fontFamily: 'monospace',
                      wordBreak: 'break-all'
                    }}>
                      {comp.filePath}
                    </div>
                  </div>
                  <div style={{
                    marginLeft: '12px',
                    fontSize: '20px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {isHighlighted ? (
                      <CheckCircleIcon style={{ width: '20px', height: '20px', color: '#10b981' }} />
                    ) : (
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #9ca3af',
                        borderRadius: '4px',
                        backgroundColor: 'transparent'
                      }} />
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
